import {
  type ConfigurationExpressionValueInOut,
  type ConfigurationFunctionValueInOut,
  isConfigurationExpressionValueInOut,
  isConfigurationFunctionValueInOut,
  isSimpleValueSetupInOut,
  type SimpleValueSetupInOut,
  type TemplateFieldValueInOut,
  type TemplateVariantInOut,
} from '@festina/configuration-general-types/configuration'
import { typeUtils } from '@festina-life/core'

// TODO: Extend the configuration portal to support copy/paste on all operation types
const supportedSources = ['template'] as const
export type ClipboardOperationSource = (typeof supportedSources)[number]

type ClipboardTemplateData = TemplateFieldValueInOut | TemplateVariantInOut

export type ClipboardTemplateFieldType =
  | 'ConfigurationExpressionValueInOut'
  | 'ConfigurationFunctionValueInOut'
  | 'SimpleValueSetupInOut'
  | 'SubTemplateValueSetupInOut'
  | 'TemplateValueSetupInOut'
  | 'TemplateVariantInOut'

const isTemplateFieldValueInOut = (
  data: ClipboardTemplateData,
): data is TemplateFieldValueInOut => data._type !== 'TemplateVariantInOut'

export const isFunctionClipboardData = (
  data: ClipboardTemplateData,
): data is ConfigurationFunctionValueInOut =>
  isTemplateFieldValueInOut(data) && isConfigurationFunctionValueInOut(data)

export type ClipboardData = {
  source: ClipboardOperationSource
  data: ClipboardTemplateData
}

export const isClipboardData = (value: unknown): value is ClipboardData =>
  typeUtils.isObject(value) &&
  'data' in value &&
  'source' in value &&
  supportedSources.some((source) => source === value['source'])

const nonMatchingAssignableTypes: Record<
  ClipboardTemplateFieldType,
  ClipboardTemplateFieldType[]
> = {
  SimpleValueSetupInOut: ['ConfigurationExpressionValueInOut'],
  ConfigurationExpressionValueInOut: ['SimpleValueSetupInOut'],
  ConfigurationFunctionValueInOut: [],
  SubTemplateValueSetupInOut: [],
  TemplateValueSetupInOut: [],
  TemplateVariantInOut: [],
}

export const areTemplateClipboardTypesAssignable = (
  typeA: ClipboardTemplateFieldType,
  typeB: ClipboardTemplateFieldType,
) => typeA === typeB || nonMatchingAssignableTypes[typeA]?.includes(typeB)

export const mappedClipboardValues = (
  data: ClipboardTemplateData,
  toType: ClipboardTemplateFieldType,
): ClipboardTemplateData => {
  if (
    !areTemplateClipboardTypesAssignable(
      data._type as ClipboardTemplateFieldType,
      toType,
    )
  ) {
    throw new Error(`Unable to map between type ${data._type} and ${toType}`)
  }

  if (data._type === toType) {
    return data
  }

  if (isTemplateFieldValueInOut(data)) {
    // oxlint-disable-next-line typescript/switch-exhaustiveness-check - deliberately only handling a subset
    switch (toType) {
      case 'SimpleValueSetupInOut':
        if (isConfigurationExpressionValueInOut(data)) {
          return {
            _type: 'SimpleValueSetupInOut',
            defaultValue: {
              ...data,
              documentation: null,
            },
            documentation: data.documentation,
          } satisfies SimpleValueSetupInOut as TemplateFieldValueInOut
        }
        break
      case 'ConfigurationExpressionValueInOut':
        if (isSimpleValueSetupInOut(data)) {
          return {
            _type: 'ConfigurationExpressionValueInOut',
            documentation: data.documentation,
            expression: data.defaultValue?.expression,
          } satisfies ConfigurationExpressionValueInOut as TemplateFieldValueInOut
        }
        break
      case 'ConfigurationFunctionValueInOut': {
        throw new Error(
          'Not implemented yet: "ConfigurationFunctionValueInOut" case',
        )
      }
      case 'SubTemplateValueSetupInOut': {
        throw new Error(
          'Not implemented yet: "SubTemplateValueSetupInOut" case',
        )
      }
      case 'TemplateValueSetupInOut': {
        throw new Error('Not implemented yet: "TemplateValueSetupInOut" case')
      }
      case 'TemplateVariantInOut': {
        throw new Error('Not implemented yet: "TemplateVariantInOut" case')
      }
    }
  }

  throw new Error(`Unsupported mapping from ${data._type} to ${toType}`)
}
